import {
  AlertTriangle,
  Car,
  Dumbbell,
  FileText,
  PhoneCall,
  Siren,
  Trees,
  Volleyball,
  Waves,
} from "lucide-react";
import {useMemo, type ReactNode} from "react";
import {Link} from "react-router-dom";

import {Button} from "@/components/ui/button";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card";
import {cn} from "@/lib/utils";
import {AmenityAccessLogSheet} from "@/src/modules/amenities/components/amenity-access-log-sheet";
import {
  GYM_CONFIG,
  POOL_CONFIG,
  RECREATION_CONFIG,
  TENNIS_CONFIG,
} from "@/src/modules/amenities/lib/amenity-config";
import {DarSheet} from "@/src/modules/dars/components/dar-sheet";
import {DispatchLogsSheet} from "@/src/modules/dispatch-logs/components/sheet";
import {IncidentSheet} from "@/src/modules/incidents/components";
import {LocationRequiredDialog} from "@/src/modules/core/components";
import {useModal} from "@/src/modules/core/hooks";
import {useLocationGatedOpen} from "@/src/modules/core/hooks/use-location-gated-open";
import {useGetHasPermissions} from "@/src/modules/core/store/me";
import {useStoredUserProperty} from "@/src/modules/core/store/user-property";
import {EmergencyShiftSheet} from "@/src/modules/shifts/components";

import {
  canQuickActionAmenitySection,
  canQuickActionDar,
  canQuickActionDispatchLog,
  canQuickActionIncident,
  canQuickActionParking,
} from "../lib/quick-action-visibility";

const AMENITY_QUICK_ACTIONS = [
  {
    section: "poolaccesslogs" as const,
    modalKey: "pool-access-sheet",
    label: "Create Pool Access Log",
    description: "Log pool access",
    icon: Waves,
    config: POOL_CONFIG,
  },
  {
    section: "tennisaccesslogs" as const,
    modalKey: "tennis-access-sheet",
    label: "Create Tennis Access Log",
    description: "Log tennis court access",
    icon: Volleyball,
    config: TENNIS_CONFIG,
  },
  {
    section: "gymaccesslogs" as const,
    modalKey: "gym-access-sheet",
    label: "Create Gym Access Log",
    description: "Log gym access",
    icon: Dumbbell,
    config: GYM_CONFIG,
  },
  {
    section: "recreationaccesslogs" as const,
    modalKey: "recreation-access-sheet",
    label: "Create Recreation Access Log",
    description: "Log recreation area access",
    icon: Trees,
    config: RECREATION_CONFIG,
  },
] as const;

interface QuickActions {
  isOnActiveShift: boolean;
  requireActiveShift?: boolean;
  variant?: "default" | "compact";
  /** Report actions hidden; only emergency shift when off shift (dispatch home). */
  emergencyOnly?: boolean;
}

type QuickActionButtonProps = {
  disabled: boolean;
  onClick?: () => void;
  href?: string;
  icon: ReactNode;
  label: string;
  description?: string;
  variant: "default" | "compact";
  className?: string;
};

function QuickActionButton({
  disabled,
  onClick,
  href,
  icon,
  label,
  description,
  variant,
  className,
}: QuickActionButtonProps) {
  if (variant === "compact") {
    return (
      <Button
        disabled={disabled}
        variant="outline"
        size="sm"
        className={cn("h-auto min-h-9 w-full justify-start gap-2 px-2.5 py-2", className)}
        onClick={onClick}
        render={href ? <Link to={href} /> : undefined}
      >
        {icon}
        <span className="truncate text-left text-sm font-medium">{label}</span>
      </Button>
    );
  }

  return (
    <Button
      disabled={disabled}
      variant="outline"
      className={cn("h-auto w-full justify-start whitespace-normal bg-transparent py-4", className)}
      onClick={onClick}
      render={href ? <Link to={href} /> : undefined}
    >
      <div className="flex w-full min-w-0 items-center gap-3">
        {icon}
        <div className="min-w-0 flex-1 text-left">
          <p className="font-medium">{label}</p>
          {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
        </div>
      </div>
    </Button>
  );
}

function EmergencyShiftAction({
  variant,
  hasReportActionsAbove,
  onOpen,
}: {
  variant: "default" | "compact";
  hasReportActionsAbove: boolean;
  onOpen: () => void;
}) {
  if (variant === "compact") {
    return (
      <Button
        variant="destructiveOutline"
        size="sm"
        className="col-span-2 h-auto min-h-9 justify-start gap-2 px-2.5 py-2"
        onClick={onOpen}
      >
        <div className="shrink-0 rounded-md bg-destructive/10 p-1">
          <Siren className="size-3.5 text-destructive" />
        </div>
        <span className="text-sm font-medium">Emergency shift</span>
      </Button>
    );
  }

  return (
    <Button
      variant="destructiveOutline"
      className={cn(
        "h-auto w-full justify-start whitespace-normal py-4",
        hasReportActionsAbove && "rounded-t-none",
      )}
      onClick={onOpen}
    >
      <div className="flex w-full min-w-0 items-center gap-3">
        <div className="shrink-0 rounded-lg bg-destructive/10 p-2">
          <Siren className="size-4 text-destructive" />
        </div>
        <div className="min-w-0 flex-1 text-left">
          <p className="font-medium">Emergency shift</p>
          <p className="text-xs text-muted-foreground">Create an emergency shift</p>
        </div>
      </div>
    </Button>
  );
}

export function QuickActions({
  isOnActiveShift,
  requireActiveShift = true,
  variant = "default",
  emergencyOnly = false,
}: QuickActions) {
  const {modalKey, onOpenModal, onCloseModal} = useModal();
  const {requestOpen, locationDialogProps, isCheckingLocation} = useLocationGatedOpen();
  const userProperty = useStoredUserProperty();
  const getHasPermission = useGetHasPermissions();

  const isDarActive = canQuickActionDar(getHasPermission);
  const isIncidentsActive = canQuickActionIncident(getHasPermission);
  const activeAmenityActions = useMemo(
    () =>
      AMENITY_QUICK_ACTIONS.filter((action) =>
        canQuickActionAmenitySection(action.section, getHasPermission),
      ),
    [getHasPermission, userProperty],
  );
  const canDispatchLog = canQuickActionDispatchLog(getHasPermission);
  const canParkingEnforcement = canQuickActionParking(getHasPermission);

  const hasReportActions =
    isDarActive ||
    isIncidentsActive ||
    activeAmenityActions.length > 0 ||
    canDispatchLog ||
    canParkingEnforcement;
  const actionsDisabled = requireActiveShift && (!isOnActiveShift || isCheckingLocation);
  const showEmergencyShift = requireActiveShift && !isOnActiveShift;

  const openGatedModal = (key: string) => {
    void requestOpen(() => onOpenModal(key));
  };

  if (emergencyOnly) {
    if (!showEmergencyShift) {
      return null;
    }

    return (
      <>
        <Card className="flex h-full min-h-0 w-full flex-col gap-0 py-4">
          <CardHeader className="shrink-0">
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <EmergencyShiftAction
              variant={variant}
              hasReportActionsAbove={false}
              onOpen={() => openGatedModal("emergency-shift-sheet")}
            />
          </CardContent>
        </Card>
        {modalKey === "emergency-shift-sheet" ? (
          <EmergencyShiftSheet open onClose={onCloseModal} />
        ) : null}
        <LocationRequiredDialog {...locationDialogProps} />
      </>
    );
  }

  const iconWrapClass =
    variant === "compact"
      ? "shrink-0 rounded-md bg-primary/10 p-1"
      : "shrink-0 rounded-lg bg-primary/10 p-2";
  const iconClass = variant === "compact" ? "size-3.5 text-primary" : "size-4 text-primary";

  const renderIcon = (Icon: typeof FileText) => (
    <div className={iconWrapClass}>
      <Icon className={iconClass} />
    </div>
  );

  const reportButtons = (
    <>
      {isDarActive ? (
        <QuickActionButton
          disabled={actionsDisabled}
          variant={variant}
          label={variant === "compact" ? "DAR" : "Create DAR"}
          description="Daily Activity Report"
          icon={renderIcon(FileText)}
          onClick={() => openGatedModal("dar-sheet")}
        />
      ) : null}
      {isIncidentsActive ? (
        <QuickActionButton
          disabled={actionsDisabled}
          variant={variant}
          label={variant === "compact" ? "Incident" : "Create Incident Report"}
          description="Document incidents"
          icon={renderIcon(AlertTriangle)}
          onClick={() => openGatedModal("incident-sheet")}
        />
      ) : null}
      {activeAmenityActions.map((action) => {
        const Icon = action.icon;
        const compactLabel = action.label
          .replace(/^Create /, "")
          .replace(/ Access Log$/, " log");

        return (
          <QuickActionButton
            key={action.section}
            disabled={actionsDisabled}
            variant={variant}
            label={variant === "compact" ? compactLabel : action.label}
            description={action.description}
            icon={renderIcon(Icon)}
            onClick={() => openGatedModal(action.modalKey)}
          />
        );
      })}
      {canDispatchLog ? (
        <QuickActionButton
          disabled={actionsDisabled}
          variant={variant}
          label={variant === "compact" ? "Dispatch log" : "Create Dispatch Log"}
          description="Log dispatch calls"
          icon={renderIcon(PhoneCall)}
          onClick={() => openGatedModal("dispatch-log-sheet")}
        />
      ) : null}
      {canParkingEnforcement ? (
        <QuickActionButton
          disabled={actionsDisabled}
          variant={variant}
          label={variant === "compact" ? "Parking" : "Parking Enforcement"}
          description="Manage citations and stored vehicles"
          href="/parking-enforcement"
          icon={renderIcon(Car)}
        />
      ) : null}
    </>
  );

  return (
    <>
      <Card
        className={cn(
          "flex w-full flex-col gap-0",
          variant === "compact" ? "py-3" : "h-full min-h-0 py-4",
        )}
      >
        <CardHeader className={cn("shrink-0", variant === "compact" && "px-4 pb-2 md:px-6")}>
          <CardTitle className={variant === "compact" ? "text-base" : undefined}>
            {variant === "compact" ? "Log activity" : "Quick Actions"}
          </CardTitle>
        </CardHeader>
        <CardContent className={variant === "compact" ? "px-4 md:px-6" : undefined}>
          <div
            className={cn(
              variant === "compact"
                ? "grid grid-cols-2 gap-2"
                : "flex flex-col gap-0",
            )}
          >
            {variant === "compact" ? (
              reportButtons
            ) : (
              <>
                {isDarActive ? (
                  <QuickActionButton
                    disabled={actionsDisabled}
                    variant={variant}
                    label="Create DAR"
                    description="Daily Activity Report"
                    icon={renderIcon(FileText)}
                    className={cn(
                      hasReportActions &&
                        (isIncidentsActive ||
                          activeAmenityActions.length > 0 ||
                          canDispatchLog ||
                          canParkingEnforcement) &&
                        "rounded-b-none border-b-0",
                    )}
                    onClick={() => openGatedModal("dar-sheet")}
                  />
                ) : null}
                {isIncidentsActive ? (
                  <QuickActionButton
                    disabled={actionsDisabled}
                    variant={variant}
                    label="Create Incident Report"
                    description="Document incidents"
                    icon={renderIcon(AlertTriangle)}
                    className={cn(
                      isDarActive ? "rounded-t-none" : null,
                      (activeAmenityActions.length > 0 || canDispatchLog || canParkingEnforcement) &&
                        "rounded-b-none border-b-0",
                    )}
                    onClick={() => openGatedModal("incident-sheet")}
                  />
                ) : null}
                {activeAmenityActions.map((action, index) => {
                  const Icon = action.icon;
                  const isFirstStacked = !isDarActive && !isIncidentsActive && index === 0;
                  const isLastAmenity = index === activeAmenityActions.length - 1;
                  const hasMoreAfterAmenity = canDispatchLog || canParkingEnforcement;

                  return (
                    <QuickActionButton
                      key={action.section}
                      disabled={actionsDisabled}
                      variant={variant}
                      label={action.label}
                      description={action.description}
                      icon={renderIcon(Icon)}
                      className={cn(
                        !isFirstStacked && "rounded-t-none",
                        !(isLastAmenity && !hasMoreAfterAmenity) && "rounded-b-none border-b-0",
                      )}
                      onClick={() => openGatedModal(action.modalKey)}
                    />
                  );
                })}
                {canDispatchLog ? (
                  <QuickActionButton
                    disabled={actionsDisabled}
                    variant={variant}
                    label="Create Dispatch Log"
                    description="Log dispatch calls"
                    icon={renderIcon(PhoneCall)}
                    className={cn(
                      (isDarActive || isIncidentsActive || activeAmenityActions.length > 0) &&
                        "rounded-t-none",
                      canParkingEnforcement && "rounded-b-none border-b-0",
                    )}
                    onClick={() => openGatedModal("dispatch-log-sheet")}
                  />
                ) : null}
                {canParkingEnforcement ? (
                  <QuickActionButton
                    disabled={actionsDisabled}
                    variant={variant}
                    label="Parking Enforcement"
                    description="Manage citations and stored vehicles"
                    href="/parking-enforcement"
                    icon={renderIcon(Car)}
                    className={cn(
                      (isDarActive ||
                        isIncidentsActive ||
                        activeAmenityActions.length > 0 ||
                        canDispatchLog) &&
                        "rounded-t-none",
                    )}
                  />
                ) : null}
              </>
            )}
            {showEmergencyShift ? (
              <EmergencyShiftAction
                variant={variant}
                hasReportActionsAbove={hasReportActions}
                onOpen={() => openGatedModal("emergency-shift-sheet")}
              />
            ) : null}
          </div>
        </CardContent>
      </Card>
      {modalKey === "dar-sheet" ? <DarSheet isVisible onClose={onCloseModal} /> : null}
      {modalKey === "incident-sheet" ? <IncidentSheet isVisible onClose={onCloseModal} /> : null}
      {modalKey === "dispatch-log-sheet" ? (
        <DispatchLogsSheet isVisible dispatchLogId={null} onClose={onCloseModal} />
      ) : null}
      {AMENITY_QUICK_ACTIONS.map((action) =>
        modalKey === action.modalKey ? (
          <AmenityAccessLogSheet
            key={action.modalKey}
            config={action.config}
            isVisible
            amenityAccessLogId={null}
            onClose={onCloseModal}
          />
        ) : null,
      )}
      {modalKey === "emergency-shift-sheet" ? (
        <EmergencyShiftSheet open onClose={onCloseModal} />
      ) : null}
      <LocationRequiredDialog {...locationDialogProps} />
    </>
  );
}
